/**
 * VariantSelects Custom Element
 * Zero-dependency Web Component for Shopify product variant selection.
 * Handles option changes, color swatches, dynamic price updates,
 * availability status, and URL history updates without full page reloads.
 */

class VariantSelects extends HTMLElement {
  #form = null;
  #productJson = null;

  constructor() {
    super();
    this.addEventListener('change', this.#onOptionChange.bind(this));
    this.addEventListener('click', this.#onSwatchClick.bind(this));
  }

  connectedCallback() {
    this.#form = this.closest('form') || document.querySelector(`#product-form-${this.dataset.section}`);
    this.#initProductJson();
    this.#updateOptions();
    this.#markUnavailableValues();
  }

  #initProductJson() {
    const jsonScript = document.querySelector(`#ProductJson-${this.dataset.section}`);
    if (jsonScript) {
      try {
        this.#productJson = JSON.parse(jsonScript.textContent);
      } catch (e) {
        console.error('Failed to parse Product JSON', e);
      }
    }
  }

  #onOptionChange(event) {
    this.#scrollToPreviewMedia(event);
    this.#updateSelectedLabels();
    this.#updateOptions();
    this.#markUnavailableValues();
    this.#updateMasterId();
    this.#toggleAddButton(true, '', true);
    this.#updatePickupAvailability();

    const currentVariant = this.#getCurrentVariant();

    if (!currentVariant) {
      this.#toggleAddButton(true, window.themeConfig?.strings?.unavailable || 'Unavailable', false);
      this.#setUnavailable();
    } else {
      this.#updateURL(currentVariant);
      this.#updateVariantInput(currentVariant);
      this.#updatePrice(currentVariant);
      this.#updateAvailability(currentVariant);
    }
  }

  #scrollToPreviewMedia(event) {
    const label = this.querySelector(`label[for="${event.target.id}"]`);
    this.#scrollToMedia(label?.dataset.previewMediaId);
  }

  #onSwatchClick(event) {
    // Clicking a swatch that's already selected doesn't fire `change`
    // (the radio's checked state doesn't change), so handle the scroll
    // here too, independent of the change-event-driven variant updates.
    const label = event.target.closest('[data-preview-media-id]');
    if (label) this.#scrollToMedia(label.dataset.previewMediaId);
  }

  #scrollToMedia(mediaId) {
    if (!mediaId) return;
    const mediaItem = document.querySelector(`[data-media-id="${mediaId}"]`);
    if (mediaItem) mediaItem.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }

  #updateSelectedLabels() {
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      const label = fieldset.querySelector('[data-selected-value]');
      if (checked && label) label.textContent = checked.value;
    });
  }

  #updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset, select'));
    this.options = fieldsets.map((fieldset) => {
      if (fieldset.tagName === 'SELECT') {
        return fieldset.value;
      }
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)?.value;
    });
  }

  // Grey out + strike through values that can't be bought with the other current
  // choices (sold out or not made in that combination). They stay tappable. (2026-10-21)
  #markUnavailableValues() {
    if (!this.#productJson || !this.options) return;
    const variants = this.#productJson.variants;
    this.querySelectorAll('fieldset').forEach((fieldset, index) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const buyable = variants.some((variant) => variant.available && variant.options.every((option, i) => {
          return i === index ? option === input.value : (this.options[i] === undefined || option === this.options[i]);
        }));
        const label = this.querySelector(`label[for="${input.id}"]`);
        if (label) label.classList.toggle('is-unavailable', !buyable);
      });
    });
  }

  #getCurrentVariant() {
    if (!this.#productJson) return null;
    return this.#productJson.variants.find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  #updateMasterId() {
    this.currentVariant = this.#getCurrentVariant();
  }

  #updateURL(variant) {
    if (!variant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${variant.id}`);
  }

  #updateVariantInput(variant) {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (input) input.value = variant.id;
    });
  }

  #formatMoney(cents, format) {
    const formatString = format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    const withDelimiters = (number, precision, thousands, decimal) => {
      precision = precision ?? 2;
      thousands = thousands ?? ',';
      decimal = decimal ?? '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    };

    let value;
    switch (match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  #updatePrice(variant) {
    const priceContainer = document.querySelector(`#price-${this.dataset.section}`);
    if (!priceContainer) return;

    const currentPrice = priceContainer.querySelector('.price-item--regular');
    const comparePrice = priceContainer.querySelector('.price-item--compare');
    const moneyFormat = window.themeConfig?.moneyFormat;

    if (currentPrice) {
      currentPrice.innerHTML = moneyFormat
        ? this.#formatMoney(variant.price, moneyFormat)
        : `$${(variant.price / 100).toFixed(2)}`;
    }

    if (comparePrice) {
      if (variant.compare_at_price > variant.price) {
        comparePrice.innerHTML = moneyFormat
          ? this.#formatMoney(variant.compare_at_price, moneyFormat)
          : `$${(variant.compare_at_price / 100).toFixed(2)}`;
        comparePrice.style.display = 'inline';
      } else {
        comparePrice.style.display = 'none';
      }
    }
  }

  #updateAvailability(variant) {
    const addButton = this.#form?.querySelector('[name="add"]');
    if (!addButton) return;

    if (variant.available) {
      this.#toggleAddButton(false, window.themeConfig?.strings?.addToCart || 'Add to cart');
    } else {
      this.#toggleAddButton(true, window.themeConfig?.strings?.soldOut || 'Sold out');
    }
  }

  #toggleAddButton(disable = true, text = '', modifyClass = true) {
    const addButton = this.#form?.querySelector('[name="add"]');
    if (!addButton) return;

    const addButtonText = addButton.querySelector('.btn__text') || addButton;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = text;
    }
  }

  #setUnavailable() {
    const priceContainer = document.querySelector(`#price-${this.dataset.section}`);
    if (priceContainer) priceContainer.classList.add('visibility-hidden');
  }

  #updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }
}

customElements.define('variant-selects', VariantSelects);
